import { listUnownedRepairMasters } from './queueHealth.js';
import { toUserMessage } from '../errors.js';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSyncEngine, type SyncState } from './engine.js';
import { useAuth } from '../auth/AuthProvider.js';
import { openLocalDb, flushLocalDb, getMeta } from '../db/local.js';
import { getSupabase } from '../supabase.js';
import { dbg } from '../debug/debugLog.js';
import { isPermanentStockCommandRejection } from './syncErrorClassification.js';
import {
  drainProductionLotPhotoOutbox,
  productionLotPhotoReviewCount as readProductionLotPhotoReviewCount,
  purgeProductionLotPhotosOutsideTenants,
  retryProductionLotPhotoReviews,
} from '../labels/productionLotPhotoOutbox.js';
import { purgeOperationalEvidenceOutsideTenants } from '../haccp/operationalEvidenceOutbox.js';

// T-111 recovery: tabelle che ammettono master records creati a mano da editor.
// Per ognuna verifichiamo che ogni riga in masters_local del tenant attivo
// esista anche sul server; quelle assenti vengono "resuscitate" come INSERT.
// Esclusi: tenants (bootstrap-only), businesses (bootstrap), documents (edge fn),
// productions (edge fn / labels flow).
const REPAIRABLE_TABLES = [
  'foods',
  'suppliers',
  'recipes',
  'storage_units',
  'probes',
  'cleaning_equipments',
  'cleaning_schedules',
  'printers',
  'label_templates',
  'staff_profiles',
];

// Le capability commerciali non fanno parte del payload sync-pull: sono lette
// direttamente con RLS. Quando il device torna disponibile, invalidiamo anche
// queste query così il tile Food Cost si riallinea senza reset di cache/dati.
const REMOTE_CAPABILITY_QUERY_KEYS = [
  ['tenant_food_cost_enabled'],
  ['tenant_food_cost_access'],
] as const;

export interface SyncContextValue extends SyncState {
  productionLotPhotoReviewCount: number;
  retryProductionLotPhotos: () => Promise<void>;
}

const SyncContext = createContext<SyncContextValue | null>(null);

export function SyncProvider({ children }: { children: ReactNode }): JSX.Element {
  const { session, profile } = useAuth();
  const engine = getSyncEngine();
  const queryClient = useQueryClient();
  const [state, setState] = useState<SyncState>(engine.getState());
  const lastPullRef = useRef<string | null>(null);
  const lastPushRef = useRef<string | null>(null);
  const cleanedTenantsKey = useRef<string | null>(null);
  const lotPhotoDrainBusy = useRef(false);
  const [lotPhotoReviewCount, setLotPhotoReviewCount] = useState(0);

  const drainLotPhotoOutbox = useCallback(async (tenantId: string): Promise<void> => {
    if (lotPhotoDrainBusy.current) return;
    lotPhotoDrainBusy.current = true;
    try {
      await drainProductionLotPhotoOutbox(tenantId);
    } catch (error) {
      dbg.warn('production-lot-photo', 'outbox_drain_failed', {
        error: toUserMessage(error),
      });
    } finally {
      try {
        setLotPhotoReviewCount(await readProductionLotPhotoReviewCount(tenantId));
      } catch (error) {
        dbg.warn('production-lot-photo', 'review_count_failed', {
          error: toUserMessage(error),
        });
      }
      lotPhotoDrainBusy.current = false;
    }
  }, []);

  const retryProductionLotPhotos = useCallback(async (): Promise<void> => {
    const tenantId = profile?.active_tenant_id;
    if (!session || !tenantId) return;
    await retryProductionLotPhotoReviews(tenantId);
    await drainLotPhotoOutbox(tenantId);
  }, [drainLotPhotoOutbox, profile?.active_tenant_id, session]);

  // T-562: le foto di lotto sono un outbox separato dai JSON di sync perché
  // contengono byte. Parte con la sessione, sopravvive al cambio pagina e
  // carica soltanto capture già collegate a una produzione durevole.
  useEffect(() => {
    const tenantId = profile?.active_tenant_id;
    if (!session || !tenantId) {
      setLotPhotoReviewCount(0);
      return;
    }
    const drain = (): void => {
      void drainLotPhotoOutbox(tenantId);
    };
    drain();
    const timer = window.setInterval(drain, 15_000);
    window.addEventListener('online', drain);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('online', drain);
    };
  }, [drainLotPhotoOutbox, profile?.active_tenant_id, session]);

  // Pulizia "orphan tenants": cancella da masters_local/events_local le righe
  // con tenant_id NON appartenenti all'utente corrente. Possono accumularsi
  // dopo reset del Supabase locale + nuovo bootstrap (la SQLCipher del telefono
  // sopravvive ai reset server). Lasciate lì, "rubano" le query con filtro
  // tenant_id rendendo il magazzino vuoto pur avendo dati sul server.
  useEffect(() => {
    if (!profile?.tenants) return;
    const validTenants = profile.tenants;
    const key = `${profile.id}:${validTenants.slice().sort().join(',')}`;
    if (cleanedTenantsKey.current === key) return;
    cleanedTenantsKey.current = key;
    void (async () => {
      try {
        const db = await openLocalDb(null);
        const placeholders = validTenants.map(() => '?').join(',');
        // events: niente fallback IS NULL, gli events hanno sempre tenant_id
        const evtRes = validTenants.length > 0
          ? await db.run(
              `DELETE FROM events_local WHERE tenant_id NOT IN (${placeholders})`,
              validTenants,
            )
          : await db.run('DELETE FROM events_local WHERE tenant_id IS NOT NULL');
        // masters: ammessi tenant_id NULL (es. ricette globali); rimuoviamo solo quelli con tenant non valido E non null
        const mstRes = validTenants.length > 0
          ? await db.run(
              `DELETE FROM masters_local WHERE tenant_id IS NOT NULL AND tenant_id NOT IN (${placeholders})`,
              validTenants,
            )
          : await db.run('DELETE FROM masters_local WHERE tenant_id IS NOT NULL');
        const photoChanges = await purgeProductionLotPhotosOutsideTenants(validTenants);
        const operationalPhotoChanges = await purgeOperationalEvidenceOutsideTenants(validTenants);
        const evtChanges = (evtRes as { changes?: { changes?: number } }).changes?.changes ?? 0;
        const mstChanges = (mstRes as { changes?: { changes?: number } }).changes?.changes ?? 0;
        await flushLocalDb();
        dbg.info('sync-cleanup', 'orphan_tenants_cleaned', {
          valid_tenants: validTenants,
          events_deleted: evtChanges,
          masters_deleted: mstChanges,
          photos_deleted: photoChanges,
          operational_photos_deleted: operationalPhotoChanges,
        });
        if (evtChanges > 0 || mstChanges > 0) {
          await queryClient.invalidateQueries({ queryKey: ['masters'] });
          await queryClient.invalidateQueries({ queryKey: ['events'] });
        }
      } catch (err) {
        dbg.error('sync-cleanup', 'orphan_cleanup_failed', {
          error: toUserMessage(err),
        });
      }
    })();
  }, [profile?.id, profile?.tenants, queryClient]);

  useEffect(() => engine.subscribe(setState), [engine]);

  // T-111 recovery: dopo la prima pull, scansiona masters_local del tenant attivo
  // e per ogni record assente dal server lo re-enqueue come `insert`. Risolve il
  // bug "master upsert silently dropped": prima del fix, qualsiasi food/supplier/ecc.
  // creato da editor mobile (FoodEditorModal & co.) restava nel solo masters_local
  // del creatore (op='upsert' che il server vecchio trattava come not_found update
  // → drop dopo 5 retry). Per Pierpaolo + clienti early adopter significava
  // assistenza consulente→cliente rotta: il consulente vedeva i dati sul suo
  // telefono, il cliente NO. Una sola passata per sessione (ref).
  const repairedRef = useRef(false);
  useEffect(() => {
    if (!state.lastPullAt) return; // aspetta che la prima pull sia andata
    if (repairedRef.current) return;
    if (!profile?.tenants || profile.tenants.length === 0) return;
    // SOLO il tenant attivo (allinea il commento sopra e — soprattutto — ciò che
    // la query RLS-bound vede davvero). sync-pull gira in service-role e popola
    // masters_local con TUTTI i tenant di claims.tenants (god-view owner), ma la
    // policy RESTRICTIVE *_scope_active_tenant restringe ogni SELECT del client
    // utente al solo jwt_active_tenant(). Confrontare masters_local di tutti i
    // tenant contro un server che ne mostra uno solo faceva sembrare "orfani" i
    // record degli altri tenant → re-enqueue insert all'infinito ad ogni avvio
    // (coda che si riempie da sola). Scoping al tenant attivo = mele con mele.
    const activeTenant = profile.active_tenant_id;
    if (!activeTenant) return; // fail-closed: senza tenant attivo, niente repair
    repairedRef.current = true;
    const validTenants = [activeTenant];
    void (async () => {
      try {
        const sb = getSupabase();
        const deviceId = (await getMeta('device_id')) ?? 'repair';
        let totalResurrected = 0;
        for (const table of REPAIRABLE_TABLES) {
          // 1) ID presenti sul server per i tenant dell'utente
          const { data: serverRows, error } = await sb
            .from(table)
            .select('id')
            .in('tenant_id', validTenants);
          if (error) {
            dbg.warn('sync-repair', `${table}_server_query_failed`, {
              error: toUserMessage(error),
            });
            continue;
          }
          const serverIds = new Set(
            ((serverRows ?? []) as Array<{ id: string }>).map((r) => r.id),
          );
          // 2) ID presenti in locale (non archiviati) per gli stessi tenant
          const localRows = await listUnownedRepairMasters(table, activeTenant);
          // 3) Orfani = locali non presenti sul server → re-enqueue come insert
          for (const local of localRows) {
            if (serverIds.has(local.id)) continue;
            let row: Record<string, unknown>;
            try {
              row = JSON.parse(local.row) as Record<string, unknown>;
            } catch {
              continue;
            }
            try {
              await engine.queueMaster({
                table, id: local.id, op: 'insert', base_version: 0,
                patch: row, device_id: deviceId, client_ts: new Date().toISOString(),
              });
              totalResurrected++;
              dbg.warn('sync-repair', 'master_resurrected_local_orphan', {
                table, id: local.id, tenant_id: activeTenant,
              });
            } catch (error) {
              // A new compound command may have claimed the master since the
              // read. Its guard stays authoritative; other repairs can continue.
              dbg.warn('sync-repair', 'master_repair_deferred', {
                table, id: local.id, tenant_id: activeTenant,
                reason: error instanceof Error ? error.message : String(error),
              });
            }
          }
        }
        if (totalResurrected > 0) {
          dbg.info('sync-repair', 'session_repair_done', {
            resurrected: totalResurrected,
          });
          // Forza un tick di push per spedire gli orfani subito (non aspettare 30s)
          void engine.tick().catch(() => undefined);
        } else {
          dbg.info('sync-repair', 'session_repair_clean', {
            checked_tables: REPAIRABLE_TABLES.length,
          });
        }
      } catch (err) {
        dbg.error('sync-repair', 'repair_failed', {
          error: toUserMessage(err),
        });
      }
    })();
  }, [state.lastPullAt, profile?.tenants, profile?.active_tenant_id, engine]);

  // Refresh local readers after actual changes. An unchanged pull still
  // updates network health, without reloading every cached Ionic page.
  useEffect(() => {
    if (state.lastPullAt && state.lastPullAt !== lastPullRef.current) {
      lastPullRef.current = state.lastPullAt;
      if (state.lastPullChangedData !== false) {
        void queryClient.invalidateQueries({ queryKey: ['masters'] });
        void queryClient.invalidateQueries({ queryKey: ['events'] });
      }
    }
    if (state.lastPushAt && state.lastPushAt !== lastPushRef.current) {
      lastPushRef.current = state.lastPushAt;
      void queryClient.invalidateQueries({ queryKey: ['masters'] });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    }
  }, [state.lastPullAt, state.lastPullChangedData, state.lastPushAt, queryClient]);

  useEffect(() => {
    engine.setOfflineSession(session);
    if (!session) return;
    void engine.start();
    return () => engine.stop();
  }, [engine, session]);

  useEffect(() => () => engine.setOfflineSession(null), [engine]);

  // Auto-retry esponenziale quando la sync è in stato 'error'.
  // Without this, lo stato 'error' persiste finché l'utente non chiude e
  // riapre l'app o non clicca manualmente "Riprova" sul banner. Con l'auto-
  // retry ricoveriamo da soli dopo: 5s, 15s, 30s, 60s, poi resta a 60s.
  // Si resetta non appena un tick va a buon fine (status passa a idle).
  const retryAttemptsRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleRetry = useCallback((attempt: number) => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    const delays = [5_000, 15_000, 30_000, 60_000];
    const delay = delays[Math.min(attempt, delays.length - 1)];
    dbg.info('sync-retry', 'scheduled', { attempt: attempt + 1, in_ms: delay });
    retryTimerRef.current = setTimeout(() => {
      // Solo se siamo ancora online; offline → aspetta di tornare online
      // (l'evento `online` rifa partire un tick autonomamente, vedi sotto).
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      void engine.tick().catch(() => undefined);
    }, delay);
  }, [engine]);

  useEffect(() => {
    const permanentStockRejection = isPermanentStockCommandRejection(state.lastError);
    if (state.status === 'error' && !permanentStockRejection) {
      scheduleRetry(retryAttemptsRef.current);
      retryAttemptsRef.current += 1;
    } else if (state.status === 'idle' || permanentStockRejection) {
      // Recovery completata, reset del counter
      retryAttemptsRef.current = 0;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    }
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [state.status, state.lastError, scheduleRetry]);

  // Quando il device torna online dopo un'interruzione di rete, forza tick.
  // Sul mobile è frequente: WiFi → cellulare → WiFi, oppure tunnel attraversato.
  useEffect(() => {
    if (!session) return;
    const onOnline = (): void => {
      dbg.info('sync-online', 'navigator_online');
      retryAttemptsRef.current = 0; // reset backoff: il device è "fresco"
      void engine.tick().catch(() => undefined);
      for (const queryKey of REMOTE_CAPABILITY_QUERY_KEYS) {
        void queryClient.invalidateQueries({ queryKey });
      }
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [engine, session]);

  // Quando la pagina torna visibile (es. utente apriva un'altra app e torna a
  // Niveo), forziamo un sync immediato + invalidiamo React Query. Senza questo,
  // il setInterval che gira il sync è messo in pausa dal sistema durante il
  // background, e al ritorno l'app può restare con dati stale finché il
  // prossimo intervallo non scatta (fino a 30s di "vuoto" percepito).
  useEffect(() => {
    if (!session) return;
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      dbg.info('sync-resume', 'visibility_visible');
      void engine.start().then(() => engine.tick()).catch(() => undefined);
      void queryClient.invalidateQueries({ queryKey: ['masters'] });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
      for (const queryKey of REMOTE_CAPABILITY_QUERY_KEYS) {
        void queryClient.invalidateQueries({ queryKey });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    let disposed = false;
    let removeNative: (() => Promise<void>) | undefined;
    void import('@capacitor/app').then(async ({ App }) => {
      const listener = await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) onVisible();
      });
      if (disposed) await listener.remove();
      else removeNative = () => listener.remove();
    }).catch(() => undefined);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisible);
      void removeNative?.();
    };
  }, [engine, queryClient, session]);

  const contextValue: SyncContextValue = {
    ...state,
    productionLotPhotoReviewCount: lotPhotoReviewCount,
    retryProductionLotPhotos,
  };

  return <SyncContext.Provider value={contextValue}>{children}</SyncContext.Provider>;
}

export function useSyncState(): SyncContextValue {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSyncState must be used within <SyncProvider>');
  return ctx;
}
